import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { ArrowLeft, Eye, Film } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminEventPreview() {
  const { admin } = useAdminAuth();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eRes, pRes] = await Promise.all([
          fetch(`${API_URL}/api/admin/events/${eventId}`, {
            headers: { Authorization: `Bearer ${admin.token}` },
          }),
          fetch(`${API_URL}/api/admin/events/${eventId}/products`, {
            headers: { Authorization: `Bearer ${admin.token}` },
          }),
        ]);
        setEvent(await eRes.json());
        setProducts(await pRes.json());
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [admin.token, eventId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#333] border-t-[#D8C08F] rounded-full animate-spin" />
      </div>
    );
  }

  if (!event) {
    return <p className="text-[#888] text-sm font-sans">Event not found.</p>;
  }

  const statusLabel =
    event.status === "published"
      ? "Live"
      : event.status === "draft"
      ? "Draft Preview"
      : "Archived";

  return (
    <div data-testid="admin-event-preview">
      <button
        onClick={() => navigate("/admin/events")}
        className="flex items-center gap-2 text-[#888] hover:text-white text-sm font-sans mb-6 transition-colors"
        data-testid="back-to-events"
      >
        <ArrowLeft size={16} />
        Back to Events
      </button>

      <div className="flex items-center gap-3 mb-6">
        <Eye size={18} className="text-[#D8C08F]" />
        <h1 className="text-white text-2xl font-sans font-semibold">
          Event Preview
        </h1>
        <span className="text-xs font-sans px-2 py-1 rounded-md bg-yellow-500/15 text-yellow-400 border border-yellow-500/20">
          {statusLabel}
        </span>
      </div>

      {/* Event Card Preview */}
      <div className="max-w-sm mb-8">
        <div className="relative rounded-[20px] overflow-hidden h-[340px] bg-[#111]">
          {event.image ? (
            <img
              src={event.image}
              alt={event.brand}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: event.objectPosition }}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-[#333] text-sm font-sans">
              No image
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
          {event.badge && (
            <div className="absolute top-4 left-4 z-10">
              <span className="inline-block px-4 py-2 bg-white/15 backdrop-blur-md rounded-lg text-white text-[11px] font-sans font-semibold tracking-[0.12em] uppercase">
                {event.badge}
              </span>
            </div>
          )}
          <div className="absolute bottom-0 left-0 right-0 p-5 z-10">
            <p className="text-white/60 text-xs font-sans mb-0.5">
              {event.subtitle}
            </p>
            <h2 className="text-white text-[26px] font-serif font-semibold tracking-tight leading-none mb-1.5">
              {event.brand}
            </h2>
            <p className="text-white/70 text-xs font-sans tracking-wide">
              Preview Mode
            </p>
          </div>
        </div>
      </div>

      {/* Products Preview */}
      <h2 className="text-white text-lg font-sans font-semibold mb-4">
        Products ({products.length})
      </h2>

      {products.length === 0 ? (
        <p className="text-[#666] text-sm font-sans">
          No products added yet.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((product) => (
            <div
              key={product.id}
              className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl overflow-hidden"
            >
              <div className="h-40 bg-[#111] relative">
                {(() => {
                  const thumb = product.image || (product.mediaType === "video" ? product.posterUrl : null);
                  if (thumb) {
                    return (
                      <img
                        src={thumb}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    );
                  }
                  if (product.mediaType === "video") {
                    return (
                      <div className="w-full h-full flex items-center justify-center bg-[#1c1c2a]">
                        <Film size={28} className="text-[#D8C08F]/50" />
                        <span className="text-[#D8C08F]/50 text-xs font-sans ml-1.5">Video</span>
                      </div>
                    );
                  }
                  return (
                    <div className="w-full h-full flex items-center justify-center text-[#333] text-xs font-sans">
                      No image
                    </div>
                  );
                })()}
                {product.mediaType === "video" && (
                  <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm rounded-md px-1.5 py-0.5 flex items-center gap-1" data-testid={`preview-video-badge-${product.id}`}>
                    <Film size={11} className="text-[#D8C08F]" />
                    <span className="text-[#D8C08F] text-[10px] font-sans font-medium">VIDEO</span>
                  </div>
                )}
              </div>
              <div className="p-4">
                <p className="text-[#888] text-[10px] font-sans uppercase tracking-wider mb-1">
                  {product.brand}
                </p>
                <h3 className="text-white text-sm font-sans font-medium mb-2 leading-tight">
                  {product.name}
                </h3>
                <div className="flex items-center gap-2">
                  <span className="text-[#555] text-xs font-sans line-through">
                    ${product.retailPrice?.toFixed(2)}
                  </span>
                  <span className="text-[#D8C08F] text-sm font-sans font-semibold">
                    ${product.salePrice?.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
